import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DayPickerRangeController, isInclusivelyAfterDay } from 'react-dates';
import { START_DATE, END_DATE } from 'react-dates/constants';
import { useDispatch, useSelector } from 'react-redux';
import { change, formValueSelector } from 'redux-form';

import { FormattedMessage } from 'react-intl';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import 'react-dates/initialize';

import CustomizableCalendarDay from './CustomizableCalendarDay';
import Loader from '../../../components/Loader';

import messages from '../../../locale/messages';
import { checkAvailability } from '../../../actions/checkAvailability';
import { getBlockedDates } from '../../../actions/Listing/getBlockedDates';
import { getSpecialPricingData } from '../../../actions/Listing/getSpecialPricingData';
import { isRTL } from '../../../helpers/formatLocale';
import { getDateUsingTimeZone } from '../../../helpers/dateRange';
import { usePrev } from '../../../hooks/usePrev';

import c from 'react-dates/lib/css/_datepicker.css';
import s from './AvailabilityCalendar.css';

const selector = formValueSelector('BookingForm');

const AvailabilityCalendar = (props) => {

  const { blockedDates, formName, queryStartDate, queryEndDate } = props;
  const { listId, country, listingData, paymentCalendar } = props;
  const { loading, containerClassName } = props;

  const [focusedInput, setFocusedInput] = useState(props.autoFocusEndDate ? END_DATE : START_DATE);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [blockedDatesSet, setBlockedDatesSet] = useState(new Set());
  const [smallDevice, setSmallDevice] = useState(false);
  const [load, setLoad] = useState(true);
  const [dynamicMinimumNights, setDynamicMinimumNights] = useState(listingData?.minNight || 1);
  const [showMinStayTooltip, setShowMinStayTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  let loadSync = null;
  const dispatch = useDispatch();
  const lastMousePosition = useRef({ x: 0, y: 0 });

  const formStartDate = useSelector(state => selector(state, 'startDate'));
  const formEndDate = useSelector(state => selector(state, 'endDate'));
  const checkIn = useSelector(state => selector(state, 'checkIn'));
  const checkOut = useSelector(state => selector(state, 'checkOut'));
  const isFullDayBlock = useSelector(state => state?.viewListing?.isFullDayBlock);
  const locale = useSelector(state => state?.intl?.locale);

  const prevLocale = usePrev(locale);

  useEffect(() => {
    const blockedDatesSet = new Set();
    let isBrowser = typeof window !== 'undefined';
    if (isBrowser) {
      handleResize();
      window.addEventListener('resize', handleResize);

    }
    isFullDayBlock?.forEach(day => {
      // we save the unique timestamp of that day
      if (day?.calendarStatus != 'available') {
        blockedDatesSet?.add(moment(day?.blockedDates).format('YYYY-MM-DD'));
      }
    });
    setBlockedDatesSet(blockedDatesSet);
    if (queryStartDate && queryEndDate) {
      setStartDate(moment(moment(queryStartDate).format('YYYY-MM-DD')));
      setEndDate(moment(moment(queryEndDate).format('YYYY-MM-DD')));
    }


    return () => {
      const componentWillUnmount = async () => {
        let isBrowser = typeof window !== 'undefined';
        if (isBrowser) {
          window.removeEventListener('resize', handleResize);
        }
        if (formStartDate && formEndDate) {

          await dispatch(getSpecialPricingData(listId, moment(formStartDate).format('YYYY-MM-DD'), moment(formEndDate).format('YYYY-MM-DD')));
          await dispatch(checkAvailability(listId, moment(formStartDate).format('YYYY-MM-DD'), moment(formEndDate).format('YYYY-MM-DD'), listingData?.maxNight, listingData?.minNight));
        }
      };
      componentWillUnmount();
    }
  }, [locale])

  useEffect(() => {

    isFullDayBlock?.forEach(day => {
      // we save the unique timestamp of that day
      if (day.calendarStatus != 'available') {
        blockedDatesSet?.add(moment(day?.blockedDates).format('YYYY-MM-DD'));
      }
    });

    setBlockedDatesSet(blockedDatesSet);

    if (formName == 'BookingForm' && formStartDate && formEndDate) {
      setStartDate(moment(moment(formStartDate).format('YYYY-MM-DD')));
      setEndDate(moment(moment(formEndDate).format('YYYY-MM-DD')));
    } else if ((formName == 'PaymentForm') && checkIn && checkOut) {
      setStartDate(moment(moment(checkIn).format('YYYY-MM-DD')));
      setEndDate(moment(moment(checkOut).format('YYYY-MM-DD')));
    } else if ((formName == 'PaymentFormModal') && queryStartDate && queryEndDate) {
      setStartDate(moment(moment(queryStartDate).format('YYYY-MM-DD')));
      setEndDate(moment(moment(queryEndDate).format('YYYY-MM-DD')));
    }

    if (!formEndDate) { setEndDate(null); }

  }, [formStartDate, formEndDate, checkIn, checkOut, formName, queryStartDate, queryEndDate, isFullDayBlock, locale]);


  useEffect(() => {

    if (locale !== prevLocale) {
      setLoad(false);

      if (loadSync) clearTimeout(loadSync);

      loadSync = setTimeout(() => {
        setLoad(true);
      }, 1);

      return () => {
        if (loadSync) clearTimeout(loadSync);
      };
    }

  }, [locale]);

  // Track global mouse position for tooltip
  useEffect(() => {
    const trackMousePosition = (event) => {
      lastMousePosition.current = {
        x: event.clientX,
        y: event.clientY
      };
    };

    document.addEventListener('mousemove', trackMousePosition);

    return () => {
      document.removeEventListener('mousemove', trackMousePosition);
    };
  }, []);

  const calculateDynamicMinimumNights = (selectedDate) => {
    if (!selectedDate || !blockedDates || !Array.isArray(blockedDates)) {
      return listingData?.minNight > 0 ? listingData?.minNight : 1;
    }

    const selectedDateString = moment(selectedDate).format('YYYY-MM-DD');

    const matchingDate = blockedDates.find(day =>
      moment(day.blockedDates).format('YYYY-MM-DD') === selectedDateString
    );

    if (matchingDate && matchingDate.specialPriceMinStay && matchingDate.specialPriceMinStay > 0) {
      return matchingDate.specialPriceMinStay;
    }

    return listingData?.minNight > 0 ? listingData?.minNight : 1;
  };

  const onDatesChange = async ({ startDate, endDate }) => {
    const dynamicMinNights = calculateDynamicMinimumNights(startDate);

    // Show tooltip when start date is selected and no end date
    const shouldShowTooltip = startDate && !endDate && focusedInput === START_DATE;

    // Use tracked mouse position for tooltip
    if (shouldShowTooltip && lastMousePosition.current) {
      console.log('[AVAILABILITY_TOOLTIP_DEBUG] Using tracked mouse position:', lastMousePosition.current);
      setTooltipPosition(lastMousePosition.current);
    }

    setStartDate(startDate);
    setEndDate(endDate);
    setDynamicMinimumNights(dynamicMinNights);
    setShowMinStayTooltip(shouldShowTooltip);

    if (formName === 'PaymentForm' || formName === 'PaymentFormModal') {
      await Promise.all([
        await dispatch(change(formName, 'checkIn', moment(startDate).format('YYYY-MM-DD'))),
        await dispatch(change(formName, 'checkOut', endDate)),
      ])
    } else {
      await Promise.all([
        await dispatch(change(formName, 'startDate', startDate)),
        await dispatch(change(formName, 'endDate', endDate)),
      ])
    }

    if ((focusedInput === END_DATE || focusedInput === START_DATE) && endDate) {
      await dispatch(getSpecialPricingData(listId, moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD')));
      await dispatch(checkAvailability(listId, moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD'), listingData?.maxNight, dynamicMinNights));
    }

    // Hide tooltip after 3 seconds
    if (shouldShowTooltip) {
      setTimeout(() => {
        setShowMinStayTooltip(false);
      }, 3000);
    }
  }

  const onFocusChange = (focusedInput) => {
    setFocusedInput(
      // Force the focusedInput to always be truthy so that dates are always selectable
      !focusedInput ? START_DATE : focusedInput,
    );
  }

  const isDayBlocked = (day) => {
    if (blockedDatesSet) {
      return blockedDatesSet.has(moment(day).format('YYYY-MM-DD'));
    } else {
      return null;
    }
  }


  const onNextMonthChange = async (e) => {
    let today, navigationDate, monthsDiff;
    today = getDateUsingTimeZone(country, true);
    navigationDate = moment(e).format('YYYY-MM-DD');
    monthsDiff = moment(navigationDate).diff(moment(today), 'months');
    //const blockedDatesSet = new Set();
    if (monthsDiff && monthsDiff > 0) {
      let isRangedMonth = Number(monthsDiff) % 5;
      if (isRangedMonth === 0) {
        const { data } = await dispatch(getBlockedDates(listId, moment(navigationDate).startOf('month').format('YYYY-MM-DD')));
        if (data?.getBlockedDatesCalendar?.length > 0) {
          data?.getBlockedDatesCalendar?.forEach(day => {
            blockedDatesSet?.add(moment(day.blockedDates).format('YYYY-MM-DD'));
          });
          setBlockedDatesSet(blockedDatesSet);
        }
      }
    }
  }

  const onPrevMonthChange = async (e) => {
    let today = getDateUsingTimeZone(country, true);
    let navigationDate = moment(e).format('YYYY-MM-DD');
    let monthsDiff = moment(navigationDate).diff(moment(today), 'months');
    let filterDate = moment(e).add(-5, 'months');

    if (monthsDiff && monthsDiff > 0) {
      let isRangedMonth = Number(monthsDiff) % 4;
      if (isRangedMonth === 0) {
        const { data } = await getBlockedDates(listId, moment(filterDate).startOf('month').format('YYYY-MM-DD'));
        if (data?.getBlockedDatesCalendar?.length > 0) {
          data?.getBlockedDatesCalendar?.forEach(day => {
            blockedDatesSet?.add(moment(day?.blockedDates).format('YYYY-MM-DD'));
          });
          setBlockedDatesSet(blockedDatesSet);
        }
      }
    }

  };

  const handleResize = (e) => {
    let isBrowser = typeof window !== 'undefined';
    let smallDevice = isBrowser ? window.matchMedia('(max-width: 767px)').matches : false;
    setSmallDevice(smallDevice);
  };

  let numberOfMonths, today, breakPoint, minNight, maxNight, maxDaysNotice, bookingNoticeTime, condition;
  numberOfMonths = (smallDevice) ? 1 : 2;
  today = getDateUsingTimeZone(country, false), condition;
  breakPoint = getDateUsingTimeZone(country, false);
  minNight = listingData?.minNight;
  maxNight = listingData?.maxNight;
  maxDaysNotice = listingData?.maxDaysNotice;
  bookingNoticeTime = listingData?.bookingNoticeTime;

  if (maxDaysNotice === 'unavailable') {
    condition = day =>
      !isInclusivelyAfterDay(day, today) ||
      isInclusivelyAfterDay(day, today)
  } else {

    if (maxDaysNotice === '3months') {
      breakPoint.add(3, 'months');
    } else if (maxDaysNotice === '6months') {
      breakPoint.add(6, 'months');
    } else if (maxDaysNotice === '9months') {
      breakPoint.add(9, 'months');
    } else if (maxDaysNotice === '12months') {
      breakPoint.add(12, 'months');
    }

    if (maxDaysNotice !== 'available') {
      condition = day =>
        !isInclusivelyAfterDay(day, today) ||
        isInclusivelyAfterDay(day, breakPoint)
    } else if (maxDaysNotice == 'available') {
      condition = day => !isInclusivelyAfterDay(day, today)
    }
  }

  // Render tooltip function
  const renderMinStayTooltip = () => {
    if (!showMinStayTooltip) return null;

    console.log('[AVAILABILITY_TOOLTIP_DEBUG] Rendering tooltip at position:', tooltipPosition);

    const tooltipText = `Minimum stay: ${dynamicMinimumNights} ${dynamicMinimumNights > 1 ? 'nights' : 'night'}`;

    const tooltipElement = (
      <div style={{
        position: 'fixed',
        left: tooltipPosition.x,
        top: tooltipPosition.y - 50, // Position above the cursor
        transform: 'translateX(-50%)',
        backgroundColor: '#333',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '4px',
        fontSize: '12px',
        whiteSpace: 'nowrap',
        zIndex: 10000,
        pointerEvents: 'none' // Prevent tooltip from interfering with mouse events
      }}>
        {tooltipText}
        <div style={{
          position: 'absolute',
          bottom: '-4px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '4px solid transparent',
          borderRight: '4px solid transparent',
          borderTop: '4px solid #333'
        }} />
      </div>
    );

    // Render tooltip as portal to document body
    return ReactDOM.createPortal(tooltipElement, document.body);
  };

  if (loading) { return <Loader type="text" /> };

  return (
    <div className={cx(s.pageContent, containerClassName)}>
      {
        !loading && <div className={cx(s.sectionContainer, s.boxContainer, s.listOneLeft, s.noPaddingLeftRight, { [s.borderTop]: paymentCalendar })}>
          {!paymentCalendar && <h1 className={cx(s.titleText, s.space2)}>
            <FormattedMessage {...messages.availability} />
          </h1>}
          {
            minNight > 0 &&
            <p className={cx({ [s.subTitle]: paymentCalendar }, s.displayInline)}><span className={cx(s.text)}> <strong>{minNight} {minNight > 1 ? <FormattedMessage {...messages.nights} /> : <FormattedMessage {...messages.night} />}{' '}</strong>
              <FormattedMessage {...messages.minimumStay} />
            </span>
            </p>
          }
          {
            maxNight > 0 &&
            <div className={s.displayInline}>
              <span className={s.dotsCss}></span>
              <p className={cx({ [s.subTitle]: paymentCalendar }, s.displayInline)}><span className={cx(s.text)}> <strong>{maxNight} {maxNight > 1 ? <FormattedMessage {...messages.nights} /> : <FormattedMessage {...messages.night} />}{' '}</strong>
                <FormattedMessage {...messages.maximumNightStay} />
              </span>
              </p>
            </div>
          }
          <div className={cx(s.availabilityCalendarWrapper, 'availabilityCalendar')}>
            {!load ? <Loader /> :
              <DayPickerRangeController
                {...props}
                focusedInput={focusedInput}
                startDate={startDate}
                endDate={endDate}
                onDatesChange={onDatesChange}
                onFocusChange={onFocusChange}
                numberOfMonths={numberOfMonths}
                isOutsideRange={condition}
                daySize={40}
                minimumNights={dynamicMinimumNights}
                onOutsideClick={() => { console.log('') }}
                renderCalendarDay={props => <CustomizableCalendarDay {...props} blockedDates={blockedDates} />} //viewListingAvailabilityCalendar and paymentSummary edit
                onPrevMonthClick={(props) => { onPrevMonthChange(props) }}
                onNextMonthClick={(props) => { onNextMonthChange(props) }}
                transitionDuration={0}
                anchorDirection={isRTL(locale) ? 'right' : 'left'}
                isRTL={isRTL(locale)}
                isDayBlocked={day => isDayBlocked(day)}
              />
            }
          </div>
        </div>
      }
      {renderMinStayTooltip()}
    </div>
  );
}

AvailabilityCalendar.propTypes = {
  listId: PropTypes.number.isRequired,
  blockedDates: PropTypes.array,
  smallDevice: PropTypes.bool,
  verySmallDevice: PropTypes.bool,
  country: PropTypes.string
};

AvailabilityCalendar.defaultProps = {
  blockedDates: [],
  listId: null,
  maxDaysNotice: 'unavailable',
  autoFocusEndDate: false,
  showInputs: false,
  keepOpenOnDateSelect: false,
  initialVisibleMonth: null,
  hideKeyboardShortcutsPanel: true,
  noBorder: true,
  startDateOffset: undefined,
  endDateOffset: undefined,
  renderCalendarDay: undefined,
  renderDayContents: null,
  minimumNights: 1,
  smallDevice: false,
  verySmallDevice: false,
  formName: 'BookingForm',
  formStartDate: null,
  formEndDate: null,
  country: '',
  paymentCalendar: false
}

export default withStyles(c, s)(AvailabilityCalendar);
